import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import NotificationProvider from './contexts/NotificationContext';
import Layout from './components/Layout';
import NetworkStatus from './components/NetworkStatus';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Customers from './pages/Customers';
import CustomerDetails from './pages/CustomerDetails';
import ProductOperations from './pages/ProductOperations';
import TransactionTracking from './pages/TransactionTracking';
import Cars from './pages/Cars';
import Settings from './pages/Settings';
import Bonuses from './pages/Bonuses';

// Protected Route component (currently unused but kept for future use)
// const ProtectedRoute = ({ children }) => {
//   const { isAuthenticated, loading } = useAuth();

//   if (loading) {
//     return (
//       <div className="min-h-screen flex items-center justify-center">
//         <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
//       </div>
//     );
//   }

//   if (!isAuthenticated) {
//     return <Navigate to="/login" replace />;
//   }

//   return children;
// };

// Permission Route component
const PermissionRoute = ({ children, permission }) => {
  const { isAuthenticated, loading, hasPermission } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasPermission(permission)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Erişim Reddedildi</h1>
          <p className="text-gray-600 mb-4">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
          <button 
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// Public Route component (redirect if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <PermissionRoute permission="dashboard">
            <Layout pageTitle="Ana Sayfa">
              <Dashboard />
            </Layout>
          </PermissionRoute>
        }
      />

      <Route
        path="/products"
        element={
          <PermissionRoute permission="products">
            <Layout pageTitle="Ürün Yönetimi">
              <Products />
            </Layout>
          </PermissionRoute>
        }
      />

      <Route
        path="/products/:productId"
        element={
          <PermissionRoute permission="products">
            <ProductDetail />
          </PermissionRoute>
        }
      />

      <Route
        path="/customers"
        element={
          <PermissionRoute permission="customers">
            <Layout pageTitle="Müşteri Yönetimi">
              <Customers />
            </Layout>
          </PermissionRoute>
        }
      />

      <Route
        path="/customers/:id"
        element={
          <PermissionRoute permission="customers">
            <CustomerDetails />
          </PermissionRoute>
        }
      />

      <Route
        path="/customers/:customerId/products/:operationId"
        element={
          <PermissionRoute permission="customers">
            <ProductOperations />
          </PermissionRoute>
        }
      />

      <Route
        path="/transactions"
        element={
          <PermissionRoute permission="transactions">
            <Layout pageTitle="Günlük İşlemler">
              <TransactionTracking />
            </Layout>
          </PermissionRoute>
        }
      />

      <Route
        path="/cars"
        element={
          <PermissionRoute permission="products">
            <Layout pageTitle="Araç Stokları">
              <Cars />
            </Layout>
          </PermissionRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <PermissionRoute permission="settings">
            <Layout pageTitle="Ayarlar">
              <Settings />
            </Layout>
          </PermissionRoute>
        }
      />

      <Route
        path="/bonuses"
        element={
          <PermissionRoute permission="settings">
            <Layout pageTitle="Çalışan Primleri">
              <Bonuses />
            </Layout>
          </PermissionRoute>
        }
      />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <AppRoutes />
          <NetworkStatus position="bottom-left" showDetails={false} />
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
};

export default App;